import React, { memo, ChangeEvent, useState, useMemo, useCallback, useEffect } from 'react'
import { Button } from 'transfix-ui/components/Button'
import { TextArea } from 'transfix-ui/components/TextArea'
import { TIME_INPUT_FORMAT } from 'transfix-ui/components/TimeInput'
import { STOP_TYPE } from 'apps/fleet-planner/constants/loads'
import { IStop, ILoad } from 'apps/fleet-planner/types/loads'
import { capitalize } from 'utils/capitalize'
import { Appointment, APPOINTMENT_VARIANT } from './Appointment'
import { Facility } from './Facility'
import { StopDetails } from './StopDetails'

interface IStopCreateProps {
  stop: IStop
  currentEditField?: string
  onEditModeRequested: (name: string) => void
  onCancel: () => void
  updateLoad: (stops: Partial<ILoad>) => void
  stopIndex: number
  stops: IStop[]
  error?: string
  loading?: boolean
  timeFormat: TIME_INPUT_FORMAT
}

export const StopCreate = memo<IStopCreateProps>(
  ({
    currentEditField,
    onCancel,
    onEditModeRequested,
    stop,
    stopIndex,
    stops,
    timeFormat,
    updateLoad,
  }) => {
    const { stopSequence, stopType } = stop || {}
    const [newStop, setNewStop] = useState(stop)

    /**
     * There should only ever be one InlineEdit component open at a time.
     * Ex: If InlineEdit1 is open, and the user click on InlineEdit2 expected behavior is for InlineEdit1 to close, and for InlineEdit2 to open.
     * StopCreate appears to be an InlineEdit component in the UI, but it isn't.
     * Purpose of useEffect below is to mimic the expected InlineEdit behavior.
     */
    useEffect(() => {
      // A change in CurrentEditField indicates that another InlineEdit has been opened
      if (currentEditField) {
        // Save that inline edit field value
        const prevCurrentEditField = currentEditField
        // Close this component and revert stops to initial state
        onCancel()
        // Open the saved inline edit component
        onEditModeRequested(prevCurrentEditField)
      }
    }, [currentEditField])

    const stopChangeHandler = useCallback((value: Partial<IStop>) => {
      setNewStop(current => ({ ...current, ...value }))
    }, [])

    const inputChangeHandler = useCallback(
      ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
        setNewStop(current => ({ ...current, instructions: value }))
      },
      []
    )

    const submitHandler = useCallback(() => {
      const updatedStops = stops.map(currentStop => {
        if (currentStop.uuid === newStop.uuid) {
          return { ...currentStop, ...newStop }
        }
        return currentStop
      }) as IStop[]
      updateLoad({ stops: updatedStops })
    }, [updateLoad, newStop, stops])

    const firstPickup = useMemo(
      () =>
        stops
          .filter((s: IStop) => s.stopType === STOP_TYPE.pickup)
          .sort((a: IStop, b: IStop) => a.stopSequence - b.stopSequence)[0],
      [stops]
    )

    return (
      <>
        <div>
          <Facility icon='search' stop={newStop} onStopChange={stopChangeHandler} />
        </div>
        <div className='mt4'>
          <Appointment
            timeFormat={timeFormat}
            stopIndex={stopIndex}
            stop={newStop}
            onStopChange={stopChangeHandler}
            variant={APPOINTMENT_VARIANT.LOAD_DETAILS}
          />
        </div>
        <div className='mt4'>
          <StopDetails
            id='stop-create'
            firstPickup={firstPickup}
            variant='load-details'
            stop={newStop}
            onStopUpdate={stopChangeHandler}
          />
        </div>
        <div className='mr0 mt4'>
          <TextArea
            id='facility-instructions'
            data-test={`stop-instructions-text-area-${stopType}-${stopSequence}`}
            label={`Internal ${capitalize(stopType)} instructions`}
            ariaLabel={`create-stop-instructions-${stopType}-${stopSequence}`}
            placeholder='Enter instructions for your driver'
            onChange={inputChangeHandler}
            size='large'
            value={newStop?.instructions || ''}
          />
        </div>
        <div className='tr'>
          <div className='mt4'>
            <Button color='tertiary' size='large' onClick={onCancel}>
              Cancel
            </Button>
            <Button color='primary' size='large' className='ml2' onClick={submitHandler}>
              Save
            </Button>
          </div>
        </div>
      </>
    )
  }
)
